/**
 * The folio domain enums, mirroring pkg/enums on the server. Values are the wire
 * strings and nothing else — every label they carry is looked up through i18n in
 * libs/folio.ts, so a rename on either side shows up as a missing translation
 * rather than as a silently wrong badge.
 */

/** pkg/enums/FolioStatus. Two states, one move, and no way back. */
export enum FolioStatus {
    Open   = 'open',
    Closed = 'closed',
}

/** pkg/enums/ChargeSource — the business origin of a line, not its technical caller. */
export enum ChargeSource {
    Room       = 'room',
    POS        = 'pos',
    Service    = 'service',
    Manual     = 'manual',
    TouristTax = 'tourist_tax',
    /** The wellness, the bike shed, the car park — posted by a facility booking. */
    Facility   = 'facility',
    /** A meal sold at the door of the dining room, to somebody whose package did not
     *  include it. Never POS: it went through no till and has no bill to reconcile. */
    Dining     = 'dining',
}

/** pkg/enums/PaymentMethod. A refund carries the method of what it reverses. */
export enum PaymentMethod {
    Cash           = 'cash',
    Card           = 'card',
    BankTransfer   = 'bank_transfer',
    Voucher        = 'voucher',
    Deposit        = 'deposit',
    OTAVirtualCard = 'ota_virtual_card',
}

/**
 * DataStructures.FolioSplit* — how much of a bill moves when it is split between the
 * cards of one booking.
 *
 * Three ways of saying one thing, because that is how the request is actually made at
 * a counter: half of it, fifty euros of it, one of three. A third typed as 33.33 % is
 * not a third, which is why the last one exists.
 */
export enum SplitMode {
    Percent = 'percent',
    Amount  = 'amount',
    Persons = 'persons',
}

/**
 * pkg/enums/FolioEvent — what can be recorded against a line besides the posting
 * itself: money taken off it, or the line taken away.
 */
export enum FolioEventKind {
    /** The sale stays on the bill and the guest pays less for it. */
    Discount = 'discount',
    /** The line comes off altogether — the car park, the breakfast. */
    Removal  = 'removal',
}

/**
 * How far a change has got. `Running` is the claim and nothing else; the API never
 * answers one, so nothing on this side has to draw it.
 */
export enum FolioEventStatus {
    PendingApproval = 'pending_approval',
    Applied         = 'applied',
    Rejected        = 'rejected',
}

/**
 * How a discount is said out loud at a desk: "give them ten per cent" and "take twenty
 * euros off". Both, because both are said, and a third of a bill typed as 33.33 % is
 * not a third.
 */
export enum DiscountMode {
    Percent = 'percent',
    Amount  = 'amount',
}
